import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import { ThemeProvider, createTheme, StyledEngineProvider } from "@mui/material/styles";
import { get, post, patch } from "../../api/api";

const NON_ISSUE_REASONS = [
  "False positive",
  "Business context justifies",
  "Already addressed",
  "Not applicable",
  "Other (please specify)",
];

const statusToken = (status) => {
  const key = (status || "").toLowerCase();
  const map = {
    completed: { bg: "#E6F9F2", color: "#1b7f4f" },
    in_progress: { bg: "#e7f0ff", color: "#2f6fca" },
    pending: { bg: "#fff8e1", color: "#b78103" },
    failed: { bg: "#fde7e7", color: "#b42318" },
    feedback_required: { bg: "#fde7d6", color: "#c56a1a" },
    open: { bg: "#fff6db", color: "#a37a07" },
  };
  return map[key] || { bg: "#eef1f6", color: "#374151" };
};

const ReviewDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resubmitting, setResubmitting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalIssueId, setModalIssueId] = useState(null);
  const [selectedReason, setSelectedReason] = useState("");
  const [customReason, setCustomReason] = useState("");
  const [submittingNonIssue, setSubmittingNonIssue] = useState(false);
  const [unchangedError, setUnchangedError] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const muiTheme = useMemo(() => createTheme(), []);

  const openSnackbar = useCallback((message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const closeSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  useEffect(() => {
    const fetchReview = async () => {
      setLoading(true);
      setUnchangedError("");
      try {
        const data = await get(`/api/v2/qualitycontrolreviews/${id}/`);
        setReview(data);
      } catch (err) {
        setError("Failed to load review");
      } finally {
        setLoading(false);
      }
    };
    fetchReview();
  }, [id]);

  const handleResubmit = async () => {
    setResubmitting(true);
    setUnchangedError("");
    try {
      await post(`/api/v2/qualitycontrolreviews/${id}/resubmit/`);
      openSnackbar("Review resubmitted!", "success");
      const data = await get(`/api/v2/qualitycontrolreviews/${id}/`);
      setReview(data);
    } catch (err) {
      if (err?.response?.status === 409) {
        const detail =
          err.response.data?.detail ||
          "Data has not changed since last review.";
        setUnchangedError(detail);
        openSnackbar(detail, "error");
      } else {
        openSnackbar(
          err?.response?.data?.detail || "Failed to resubmit review.",
          "error"
        );
      }
    } finally {
      setResubmitting(false);
    }
  };

  const openNonIssueModal = (issueId) => {
    setModalIssueId(issueId);
    setSelectedReason("");
    setCustomReason("");
    setModalOpen(true);
  };

  const handleSubmitNonIssue = async () => {
    if (!modalIssueId) return;
    setSubmittingNonIssue(true);
    const note =
      selectedReason === "Other (please specify)" ? customReason : selectedReason;
    if (!note || note.trim() === "") {
      openSnackbar("Please provide a reason.", "warning");
      setSubmittingNonIssue(false);
      return;
    }
    try {
      await patch(`/api/v2/reviewissues/${modalIssueId}/`, {
        status: "resolved",
        resolution_note: note,
      });
      setReview((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          issues: prev.issues.map((issue) =>
            issue.id === modalIssueId
              ? { ...issue, status: "resolved", resolution_note: note }
              : issue
          ),
        };
      });
      openSnackbar("Marked as non-issue!", "success");
      setModalOpen(false);
      setModalIssueId(null);
    } catch (err) {
      openSnackbar(
        err?.response?.data?.detail || "Failed to mark as non-issue.",
        "error"
      );
    } finally {
      setSubmittingNonIssue(false);
    }
  };

  const submissionNumber = review?.submission_count || 1;
  const isInProgress =
    review && (review.status || "").toLowerCase() === "in_progress";

  const statusChip = statusToken(review?.status);

  let content = null;
  if (loading) {
    content = (
      <Stack alignItems="center" justifyContent="center" sx={{ pt: 10 }}>
        <CircularProgress />
      </Stack>
    );
  } else if (error) {
    content = (
      <Typography color="error" sx={{ pt: 10, textAlign: "center" }}>
        {error}
      </Typography>
    );
  } else if (review) {
    content = (
      <Container maxWidth="lg" sx={{ pt: 8, pb: 6 }}>
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: "1px solid #e5e7eb",
            boxShadow: "0 8px 18px rgba(0,0,0,0.06)",
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Stack spacing={3}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="h5" fontWeight={700}>
                  Review Details
                </Typography>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <IconButton
                    aria-label="Back"
                    onClick={() => navigate(-1)}
                    size="small"
                    sx={{ border: "1px solid #d1d5db" }}
                  >
                    <ArrowBackIcon fontSize="small" />
                  </IconButton>
                  <Button
                    variant="contained"
                    onClick={handleResubmit}
                    disabled={isInProgress}
                    sx={{
                      textTransform: "none",
                      bgcolor: "#d56b23",
                      "&:hover": { bgcolor: "#c55f1d" },
                      opacity: isInProgress ? 0.7 : 1,
                    }}
                  >
                    {resubmitting ? "Resubmitting..." : "Resubmit"}
                  </Button>
                </Stack>
              </Stack>

              <Stack spacing={1} alignItems="flex-start" sx={{ pl: 0 }}>
                <Stack spacing={1} alignItems="flex-start">
                <Stack direction="row" spacing={1.2} alignItems="center">
                  <Typography fontWeight={700} fontSize={16} lineHeight={1.3}>
                    Review ID:
                  </Typography>
                  <Typography fontWeight={700} fontSize={16} lineHeight={1.3}>
                    {review.id}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1.2} alignItems="center">
                  <Typography color="text.secondary" fontSize={15} lineHeight={1.3}>
                    <strong>Customer:</strong>
                  </Typography>
                  <Typography color="text.secondary" fontSize={15} lineHeight={1.3}>
                    {review.customer_name || review.customer}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1.2} alignItems="center">
                  <Typography color="text.secondary" fontSize={15} lineHeight={1.3}>
                    <strong>Status:</strong>
                  </Typography>
                  <Chip
                    label={review.status || "-"}
                    size="small"
                    sx={{
                      bgcolor: statusChip.bg,
                      color: statusChip.color,
                      fontWeight: 700,
                      borderRadius: "12px",
                      "& .MuiChip-label": { px: 1.6, textTransform: "uppercase" },
                    }}
                  />
                </Stack>
                <Stack direction="row" spacing={1.2} alignItems="center">
                  <Typography color="text.secondary" fontSize={15} lineHeight={1.3}>
                    <strong>Submission:</strong>
                  </Typography>
                  <Typography color="text.secondary" fontSize={15} lineHeight={1.3}>
                    #{submissionNumber}
                  </Typography>
                </Stack>
                {unchangedError && (
                  <Typography color="text.secondary" fontSize={13}>
                    {unchangedError}
                  </Typography>
                )}
                </Stack>
              </Stack>

              <Divider />

              <Box>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Issues
                </Typography>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#f2f6ff" }}>
                      {["Description", "Type", "Status", "Resolution Note", "Actions"].map(
                        (label) => (
                          <TableCell
                            key={label}
                            sx={{
                              fontWeight: 700,
                              textTransform: "uppercase",
                              fontSize: 12,
                              color: "#54607a",
                            }}
                          >
                            {label}
                          </TableCell>
                        )
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {review.issues && review.issues.length > 0 ? (
                      [...review.issues]
                        .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
                        .map((issue) => {
                          const badge = statusToken(issue.status);
                          const statusLabel = issue.status || "-";
                          return (
                            <TableRow key={issue.id} hover>
                              <TableCell sx={{ maxWidth: 520 }}>
                                {issue.description}
                              </TableCell>
                              <TableCell sx={{ textTransform: "lowercase" }}>
                                {issue.issue_type}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={statusLabel}
                                  size="small"
                                  sx={{
                                    bgcolor: badge.bg,
                                    color: badge.color,
                                    fontWeight: 700,
                                    borderRadius: "10px",
                                    "& .MuiChip-label": {
                                      px: 1.2,
                                      textTransform: "uppercase",
                                    },
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                {issue.resolution_note && issue.resolution_note.trim()
                                  ? issue.resolution_note
                                  : "-"}
                              </TableCell>
                              <TableCell>
                                {(issue.status || "").toLowerCase() !== "resolved" && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => openNonIssueModal(issue.id)}
                                    sx={{
                                      textTransform: "none",
                                      borderColor: "#c3cad5",
                                      color: "#495365",
                                      bgcolor: "#f8fafc",
                                      "&:hover": { borderColor: "#aeb7c3" },
                                    }}
                                  >
                                    Ignore
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                          <Typography color="text.secondary" fontStyle="italic">
                            No issues found.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={muiTheme}>
        {content}

        <Dialog open={modalOpen} onClose={() => setModalOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
            Mark as Non-Issue
          <IconButton
            aria-label="close"
            onClick={() => setModalOpen(false)}
            sx={{ ml: "auto" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <FormControl component="fieldset" required fullWidth>
            <FormLabel component="legend">
              Reason for marking as non-issue
            </FormLabel>
            <RadioGroup
              value={selectedReason}
              onChange={(e) => setSelectedReason(e.target.value)}
            >
              <Stack spacing={1.2} sx={{ mt: 1 }}>
                {NON_ISSUE_REASONS.map((reason) => (
                  <FormControlLabel
                    key={reason}
                    value={reason}
                    control={<Radio />}
                    label={reason}
                  />
                ))}
              </Stack>
            </RadioGroup>
          </FormControl>
          {selectedReason === "Other (please specify)" && (
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Custom reason"
              value={customReason}
              onChange={(e) => setCustomReason(e.target.value)}
              sx={{ mt: 2 }}
              required
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)} variant="text">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitNonIssue}
            disabled={submittingNonIssue}
          >
            {submittingNonIssue ? "Saving..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          onClose={closeSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={closeSnackbar}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

export default ReviewDetails;
